// Site detail page — state-driven hero so the customer always sees ONE
// primary action that fits the current state of the site:
//
//   no-build → "Build my site"
//   building → "Watch progress"
//   draft    → "Edit site" (and: Preview / Publish live)
//   active   → "Edit site" (and: Open live site)
//
// The word "Preview" only ever means *open the rendered site*. Building is
// always "Build" / "Rebuild" / "Watch progress." One verb per concept.

import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, ApiError, type Site as SiteRow } from "../lib/api";
import { forgePreviewUrl, siteHost, siteUrl } from "../lib/domains";
import { siteDisplayState } from "../lib/siteLifecycle";
import CancelBuildButton from "../components/CancelBuildButton";
import DeleteSiteButton from "../components/DeleteSiteButton";
import UpgradeModal, { type UpgradeReason } from "../features/billing/UpgradeModal";

export const siteState = siteDisplayState;

function fmtRelative(iso: string | null): string {
  if (!iso) return "";
  const ms = Date.now() - new Date(iso).getTime();
  const min = Math.round(ms / 60000);
  if (min < 1)  return "just now";
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24)  return `${hr}h ago`;
  const days = Math.round(hr / 24);
  return `${days}d ago`;
}

export default function Site() {
  const { siteId } = useParams<{ siteId: string }>();
  const navigate = useNavigate();
  const [site, setSite] = useState<SiteRow | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [paywallReason, setPaywallReason] = useState<UpgradeReason | null>(null);
  const [busy, setBusy] = useState<"build" | "publish" | "iterate" | null>(null);
  const [iterateOpen, setIterateOpen] = useState(false);
  const [iterateMsg, setIterateMsg] = useState("");
  const [copied, setCopied] = useState(false);

  // A build/iterate that comes back 402 is a spent meter, not an error — open
  // the upgrade popup (plans + checkout) instead of a red card. The API tags
  // which limit was hit via payload.paywall.kind (site | edit | rebuild).
  function handleSpendError(e: any, fallback: string) {
    if (e instanceof ApiError && e.status === 402) {
      const kind = (e.payload as { paywall?: { kind?: UpgradeReason } } | null)?.paywall?.kind;
      setPaywallReason(kind ?? "rebuild");
      return;
    }
    setErr(e?.payload?.error || fallback);
  }

  useEffect(() => {
    if (!siteId) return;
    api.getSite(siteId).then(setSite).catch((e) => setErr(String(e)));
  }, [siteId]);

  useEffect(() => {
    if (!siteId || !site?.pendingBuildId) return;
    let stopped = false;
    const timer = window.setInterval(async () => {
      try {
        const next = await api.getSite(siteId);
        if (!stopped) setSite(next);
      } catch {
        // Keep the last known state; the next poll retries reconciliation.
      }
    }, 3000);
    return () => { stopped = true; window.clearInterval(timer); };
  }, [site?.pendingBuildId, siteId]);

  if (err) return <div className="card" style={{ borderColor: "#fee2e2", color: "#b91c1c" }}>{err}</div>;
  if (!site) return <div className="muted">Loading…</div>;

  const state = siteState(site);
  const previewUrl = site.previewBuildId ? forgePreviewUrl(site.previewBuildId) : null;
  const liveUrl    = site.status === "active" ? siteUrl(site.subdomain) : null;

  async function build() {
    if (!siteId) return;
    setBusy("build");
    setErr(null);
    try {
      const { jobId } = await api.startBuild(siteId, "preview");
      navigate(`/sites/${siteId}/builds/${jobId}`);
    } catch (e: any) {
      handleSpendError(e, `Couldn't start a build: ${String(e)}`);
    } finally {
      setBusy(null);
    }
  }

  async function publish() {
    if (!siteId || !site?.previewBuildId) return;
    setBusy("publish");
    setErr(null);
    try {
      const updated = await api.publish(siteId, site.previewBuildId);
      setSite(updated);
    } catch (e: any) {
      setErr(e?.payload?.error || String(e));
    } finally {
      setBusy(null);
    }
  }

  async function iterate() {
    if (!siteId || !iterateMsg.trim()) return;
    setBusy("iterate");
    setErr(null);
    try {
      const { jobId } = await api.startBuild(siteId, "iterate", iterateMsg.trim());
      navigate(`/sites/${siteId}/builds/${jobId}`);
    } catch (e: any) {
      handleSpendError(e, String(e));
    } finally {
      setBusy(null);
    }
  }

  async function copyLive() {
    if (!liveUrl) return;
    try {
      await navigator.clipboard.writeText(liveUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1400);
    } catch { /* ignore */ }
  }

  return (
    <div className="stack site-workspace">
      <div className="row between" style={{ alignItems: "flex-start" }}>
        <div className="stack" style={{ gap: 4 }}>
          <h1 style={{ margin: 0, fontSize: 24 }}>{siteHost(site.subdomain)}</h1>
          <div className="muted" style={{ fontSize: 13 }}>
            {state === "active"   && <>Live · last published {fmtRelative(site.publishedAt)}</>}
            {state === "draft"    && <>Draft ready · not yet published</>}
            {state === "building" && <>Building…</>}
            {state === "no-build" && <>Not built yet</>}
            {state === "needs-attention" && <>Build needs attention</>}
          </div>
        </div>
      </div>

      {/* STATE-DRIVEN HERO */}
      {state === "no-build" && (
        <div className="card stack" style={{ padding: 24, gap: 12 }}>
          <strong style={{ fontSize: 17 }}>Your site hasn't been built yet.</strong>
          <div className="muted" style={{ fontSize: 14 }}>
            We'll build your first draft in the background. You can follow its progress as it takes shape.
          </div>
          <div>
            <button className="btn primary" disabled={busy === "build"} onClick={build}>
              {busy === "build" ? "Starting…" : "Build my site"}
            </button>
          </div>
        </div>
      )}

      {state === "building" && (site.pendingBuildId || site.previewBuildId) && (
        <>
        <Link
          to={`/sites/${siteId}/builds/${site.pendingBuildId ?? site.previewBuildId}`}
          className="card row"
          style={{
            padding: 18, gap: 14, alignItems: "center",
            background: "linear-gradient(180deg, #ffffff 0%, #f0fdf4 100%)",
            borderColor: "#bbf7d0", textDecoration: "none", color: "inherit",
          }}
        >
          <div className="builder-spinner" aria-hidden />
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: 15 }}>Your site is being built…</div>
            <div className="muted" style={{ fontSize: 13, marginTop: 2 }}>
              Watch progress while we build and check your draft.
              {liveUrl && <> Your live site is still up at <strong>{siteHost(site.subdomain)}</strong>.</>}
            </div>
          </div>
          <span className="muted">→</span>
        </Link>
        <div className="row" style={{ justifyContent: "flex-end" }}>
          <CancelBuildButton
            site={site}
            onCancelled={() => { if (siteId) api.getSite(siteId).then(setSite).catch(() => {}); }}
          />
        </div>
        </>
      )}

      {state === "draft" && previewUrl && (
        <div className="card stack" style={{ padding: 20, gap: 12 }}>
          <div className="stack" style={{ gap: 4 }}>
            <strong style={{ fontSize: 16 }}>Your draft is ready.</strong>
            <div className="muted" style={{ fontSize: 13 }}>
              {site.status === "ready_unverified"
                ? "The public preview check was delayed, but this uploaded draft is ready to edit."
                : "Review your draft, then publish it to your website address."}
            </div>
          </div>
          <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
            <Link to={`/sites/${siteId}/editor`} className="btn primary">
              Edit site
            </Link>
            <a
              href={previewUrl}
              target="_blank"
              rel="noreferrer"
              className="btn"
            >
              Open preview ↗
            </a>
            <button
              className="btn"
              onClick={publish}
              disabled={busy === "publish"}
            >
              {busy === "publish" ? "Publishing…" : "Publish live"}
            </button>
            <button
              className="btn"
              onClick={build}
              disabled={busy === "build"}
              title="Throw away the current draft and start a fresh build from your brief."
            >
              {busy === "build" ? "Starting…" : "Rebuild"}
            </button>
          </div>
          <div className="muted" style={{ fontSize: 12, wordBreak: "break-all" }}>
            {previewUrl}
          </div>
        </div>
      )}

      {state === "needs-attention" && (
        <div className="card stack" style={{ padding: 20, gap: 12, borderColor: "#fed7aa" }}>
          <strong style={{ fontSize: 16 }}>
            {site.status === "ready_unverified" ? "Your draft was built, but its preview could not be verified." : "Your last build did not finish."}
          </strong>
          <div className="muted" style={{ fontSize: 13 }}>
            Your site and brief are safe. Nothing was published or charged twice.
          </div>
          <div>
            <button className="btn primary" disabled={busy === "build"} onClick={build}>
              {busy === "build" ? "Restarting…" : "Try build again"}
            </button>
          </div>
        </div>
      )}

      {state === "active" && liveUrl && (
        <div className="card stack" style={{ padding: 20, gap: 14 }}>
          <div className="row between" style={{ alignItems: "center", flexWrap: "wrap", gap: 12 }}>
            <a
              href={liveUrl}
              target="_blank"
              rel="noreferrer"
              style={{ fontWeight: 600, fontSize: 16, wordBreak: "break-all" }}
            >
              {siteHost(site.subdomain)}
            </a>
            <button
              type="button"
              className="btn"
              onClick={copyLive}
              style={{ fontSize: 12, padding: "4px 10px" }}
            >
              {copied ? "✓ copied" : "copy"}
            </button>
          </div>
          <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
            <Link to={`/sites/${siteId}/editor`} className="btn primary">
              Edit site
            </Link>
            <a
              href={liveUrl}
              target="_blank"
              rel="noreferrer"
              className="btn"
            >
              Open live site ↗
            </a>
            {previewUrl && site.previewBuildId !== site.liveBuildId && (
              <a href={previewUrl} target="_blank" rel="noreferrer" className="btn">Preview changes ↗</a>
            )}
            <button
              className="btn"
              onClick={() => setIterateOpen((v) => !v)}
            >
              Make changes…
            </button>
            <button
              className="btn"
              onClick={build}
              disabled={busy === "build"}
              title="Build a fresh version from your brief — your live site stays up until you publish the new one."
            >
              {busy === "build" ? "Starting…" : "Rebuild"}
            </button>
          </div>
          {iterateOpen && (
            <div className="stack" style={{ gap: 8, paddingTop: 6 }}>
              <textarea
                placeholder="What should change? E.g. 'make the hero photo bigger and switch the CTA to dark green'"
                value={iterateMsg}
                onChange={(e) => setIterateMsg(e.target.value)}
                rows={3}
                style={{ padding: "8px 10px", borderRadius: 8, border: "1px solid var(--border)", fontFamily: "inherit", fontSize: 13 }}
              />
              <div className="row" style={{ gap: 8 }}>
                <button
                  className="btn primary"
                  onClick={iterate}
                  disabled={!iterateMsg.trim() || busy === "iterate"}
                >
                  {busy === "iterate" ? "Sending…" : "Send change"}
                </button>
                <button className="btn" onClick={() => { setIterateOpen(false); setIterateMsg(""); }}>
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Ancillary tools live here; editing and preview stay in the state card. */}
      <div className="row" style={{ gap: 8, flexWrap: "wrap", marginTop: 4 }}>
        {(state === "draft" || state === "active") && (
          <Link to={`/sites/${site.id}/plugins`} className="btn">Plugins &amp; integrations</Link>
        )}
        <Link to={`/sites/${site.id}/builds`} className="btn">Build history</Link>
      </div>

      <div className="row" style={{ marginTop: 24, justifyContent: "flex-end" }}>
        <DeleteSiteButton site={site} />
      </div>

      <UpgradeModal
        open={paywallReason !== null}
        reason={paywallReason ?? "site"}
        onClose={() => setPaywallReason(null)}
      />
    </div>
  );
}
